if (!customElements.get('hpg-force-distance')) {
  const MAX_FORCE_LBS = 90;
  const CURVE_SAMPLES = 40;

  customElements.define(
    'hpg-force-distance',
    class HpgForceDistance extends HTMLElement {
      connectedCallback() {
        this.chart = null;
        this.observer = null;
        this.revealTimer = null;
        this.revealFrame = null;
        this.lineRevealDelay = 520;
        this.lineProgress = 1;
        this.hasRevealed = false;
        this.canvas = this.querySelector('.hpg-force-distance__chart');
        this.dataNode = this.querySelector('.hpg-force-distance__data');
        this.section = this.closest('.hpg-force-distance');
        this.modal = this.section?.querySelector('modal-dialog');
        this.handleModalOpen = this.handleModalOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleReveal = this.handleReveal.bind(this);
        this.initChart();
        this.bindVideo();
      }

      disconnectedCallback() {
        window.clearTimeout(this.revealTimer);
        if (this.revealFrame) window.cancelAnimationFrame(this.revealFrame);
        if (this.observer) this.observer.disconnect();
        if (this.chart) this.chart.destroy();
        if (this.modal) {
          this.modal.removeEventListener('click', this.handleModalClose);
          this.modal.removeEventListener('transitionend', this.handleModalOpen);
        }
      }

      async initChart() {
        if (!this.canvas || !this.dataNode) return;

        const data = this.parseData();
        if (!data.points.length) return;

        const Chart = await this.waitForChart();
        if (!Chart || !this.isConnected) return;

        const styles = getComputedStyle(this);
        const textColor = styles.getPropertyValue('--hpg-text').trim() || '#1d1e1f';
        const mutedColor = styles.getPropertyValue('--hpg-muted').trim() || '#616161';
        const hpgColor = styles.getPropertyValue('--hpg-accent').trim() || '#2c6fe8';
        const competitorColor = styles.getPropertyValue('--hpg-force-competitor').trim() || '#9aa0a6';
        const borderColor = styles.getPropertyValue('--hpg-border').trim() || 'rgba(255,255,255,0.22)';
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isMobileChart = window.matchMedia('(max-width: 749px)').matches;
        const isCompactChart = window.matchMedia('(max-width: 1199px)').matches;
        const hpgCurve = this.buildCurve(data.points, 'hpg');
        const competitorCurve = this.buildCurve(data.points, 'competitor');
        this.lineProgress = reduceMotion ? 1 : 0;
        const lineRevealPlugin = {
          id: 'hpgForceDistanceLineReveal',
          beforeDatasetsDraw: (chart) => {
            const progress = chart.$hpgLineProgress ?? this.lineProgress;
            chart.$hpgLineClipActive = progress < 1;
            if (!chart.$hpgLineClipActive) return;

            const { ctx, chartArea } = chart;
            ctx.save();
            ctx.beginPath();
            ctx.rect(chartArea.left, chartArea.top, chartArea.width * progress, chartArea.height);
            ctx.clip();
          },
          afterDatasetsDraw: (chart) => {
            if (!chart.$hpgLineClipActive) return;
            chart.ctx.restore();
            chart.$hpgLineClipActive = false;
          }
        };

        this.chart = new Chart(this.canvas, {
          type: 'line',
          plugins: [lineRevealPlugin],
          data: {
            datasets: [
              {
                label: data.hpgLabel,
                data: hpgCurve,
                borderColor: hpgColor,
                backgroundColor: hpgColor,
                borderWidth: 4,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0.32
              },
              {
                label: data.competitorLabel,
                data: competitorCurve,
                borderColor: competitorColor,
                backgroundColor: competitorColor,
                borderWidth: 3,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 0,
              easing: 'easeOutQuart'
            },
            interaction: {
              intersect: false,
              mode: 'index',
              axis: 'x'
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                position: 'nearest',
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                borderColor,
                borderWidth: 1,
                titleColor: '#1d1e1f',
                bodyColor: '#1d1e1f',
                displayColors: true,
                usePointStyle: true,
                padding: isMobileChart ? 10 : 12,
                caretPadding: 10,
                callbacks: {
                  title: (items) => this.formatRangeOfMotion(items[0]?.parsed?.x),
                  label: (context) => `${context.dataset.label}: ${this.formatPounds(context.parsed.y)}`
                }
              }
            },
            scales: {
              x: {
                type: 'linear',
                min: 0,
                max: 100,
                title: {
                  display: Boolean(data.xLabel),
                  text: data.xLabel,
                  color: mutedColor,
                  font: { weight: 700 }
                },
                grid: {
                  display: false,
                  drawBorder: false
                },
                border: {
                  display: true,
                  color: borderColor
                },
                ticks: {
                  color: mutedColor,
                  maxRotation: 0,
                  stepSize: 25,
                  callback: (value) => {
                    if (Number(value) === 0) return 'Start';
                    if (Number(value) === 100) return 'End';
                    return '';
                  }
                }
              },
              y: {
                min: 0,
                max: MAX_FORCE_LBS,
                afterFit: (scale) => {
                  if (isCompactChart) scale.width = isMobileChart ? 54 : 60;
                },
                title: {
                  display: Boolean(data.yLabel),
                  text: data.yLabel,
                  color: mutedColor,
                  font: { weight: 700 },
                  padding: isCompactChart ? (isMobileChart ? 2 : 4) : 8
                },
                grid: {
                  display: false,
                  drawBorder: false
                },
                border: {
                  display: true,
                  color: borderColor
                },
                ticks: {
                  color: mutedColor,
                  padding: isCompactChart ? (isMobileChart ? 2 : 4) : 8,
                  stepSize: 15,
                  callback: (value) => (Number(value) === MAX_FORCE_LBS ? `${MAX_FORCE_LBS} lbs` : '')
                }
              }
            },
            color: textColor
          }
        });

        this.fullChartData = { hpgCurve, competitorCurve };
        this.bindReveal(reduceMotion);
      }

      parseData() {
        try {
          const data = JSON.parse(this.dataNode.textContent);
          return {
            xLabel: data.xLabel || '',
            yLabel: data.yLabel || '',
            hpgLabel: data.hpgLabel || 'HomeProGym',
            competitorLabel: data.competitorLabel || 'Cheap bands',
            points: Array.isArray(data.points)
              ? data.points
                  .filter((point) => point && point.label !== undefined)
                  .map((point) => ({
                    label: point.label,
                    distance: Number(point.distance) || 0,
                    hpg: Math.min(Math.max(Number(point.hpg) || 0, 0), MAX_FORCE_LBS),
                    competitor: Math.min(Math.max(Number(point.competitor) || 0, 0), MAX_FORCE_LBS)
                  }))
              : []
          };
        } catch (error) {
          return {
            xLabel: '',
            yLabel: '',
            hpgLabel: 'HomeProGym',
            competitorLabel: 'Cheap bands',
            points: []
          };
        }
      }

      buildCurve(points, key) {
        const orderedPoints = [...points].sort((a, b) => a.distance - b.distance);
        const endPoint = orderedPoints.at(-1);
        const maxDistance = endPoint?.distance || 100;

        return Array.from({ length: CURVE_SAMPLES + 1 }, (_, index) => {
          const progress = index / CURVE_SAMPLES;
          const distance = progress * maxDistance;
          return {
            x: Number(distance.toFixed(2)),
            y: this.interpolateForce(orderedPoints, distance, key),
            label: index === 0
              ? orderedPoints[0]?.label || 'Start'
              : index === CURVE_SAMPLES
                ? endPoint?.label || 'End'
                : ''
          };
        });
      }

      interpolateForce(points, distance, key) {
        if (!points.length) return 0;
        if (distance <= points[0].distance) return points[0][key];

        const nextIndex = points.findIndex((point) => point.distance >= distance);
        if (nextIndex === -1) return points.at(-1)[key];

        const previousPoint = points[nextIndex - 1];
        const nextPoint = points[nextIndex];
        const span = nextPoint.distance - previousPoint.distance;
        if (span <= 0) return nextPoint[key];

        const progress = (distance - previousPoint.distance) / span;
        const force = previousPoint[key] + (nextPoint[key] - previousPoint[key]) * progress;
        return Number(force.toFixed(2));
      }

      formatRangeOfMotion(value) {
        const percentage = Math.round(Number(value) || 0);
        if (percentage === 0) return 'Start of range';
        if (percentage === 100) return 'End of range';
        return `${percentage}% range of motion`;
      }

      formatPounds(value) {
        return `${Math.round(Number(value) || 0)} lbs`;
      }

      waitForChart() {
        if (window.Chart) return Promise.resolve(window.Chart);

        return new Promise((resolve) => {
          let attempts = 0;
          const timer = window.setInterval(() => {
            attempts += 1;
            if (window.Chart || attempts > 80) {
              window.clearInterval(timer);
              resolve(window.Chart);
            }
          }, 50);
        });
      }

      bindReveal(reduceMotion) {
        if (!this.section || !this.chart) return;

        if (reduceMotion || !('IntersectionObserver' in window)) {
          this.section.classList.add('is-force-content-visible', 'is-force-lines-visible');
          this.drawLines(0);
          return;
        }

        this.section.classList.add('hpg-force-distance--animate');
        const isCompactViewport = window.matchMedia('(max-width: 989px)').matches;
        this.observer = new IntersectionObserver(this.handleReveal, {
          rootMargin: isCompactViewport ? '-30% 0px -35% 0px' : '-28% 0px -32% 0px',
          threshold: 0
        });
        this.observer.observe(this.section);
      }

      handleReveal(entries) {
        if (this.hasRevealed) return;
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.hasRevealed = true;
        this.observer?.disconnect();
        this.section.classList.add('is-force-content-visible');
        this.revealTimer = window.setTimeout(() => {
          this.section.classList.add('is-force-lines-visible');
          this.drawLines(1200);
        }, this.lineRevealDelay);
      }

      drawLines(duration) {
        if (!this.chart || !this.fullChartData) return;
        if (this.revealFrame) window.cancelAnimationFrame(this.revealFrame);
        if (!duration) {
          this.lineProgress = 1;
          this.chart.$hpgLineProgress = 1;
          this.chart.draw();
          return;
        }

        const startedAt = performance.now();
        const animate = (timestamp) => {
          const elapsed = timestamp - startedAt;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.lineProgress = eased;
          this.chart.$hpgLineProgress = eased;
          this.chart.draw();

          if (progress < 1) {
            this.revealFrame = window.requestAnimationFrame(animate);
          }
        };

        this.revealFrame = window.requestAnimationFrame(animate);
      }

      bindVideo() {
        if (!this.modal) return;
        this.modal.addEventListener('click', this.handleModalClose);
        this.modal.addEventListener('transitionend', this.handleModalOpen);
      }

      handleModalOpen() {
        if (!this.modal?.hasAttribute('open')) return;
        const video = this.modal.querySelector('video');
        if (video) video.play().catch(() => {});
      }

      handleModalClose(event) {
        if (!event.target.closest('.modal-video__toggle')) return;
        const video = this.modal.querySelector('video');
        if (video) video.pause();
      }
    }
  );
}
